/**
 * ExportMenu
 *
 * A drop-in export control usable from any list view, table view, or detail page.
 * Provides CSV, Excel, and JSON download options backed by the existing
 * exportToCsv (reportExport.ts) and exportToExcel (excelService.ts) utilities.
 *
 * Usage — minimal:
 *   <ExportMenu data={rows} filename="items" />
 *
 * Usage — with column projection:
 *   <ExportMenu
 *     data={rows}
 *     filename="session-results"
 *     columns={['item_id', 'rank', 'score']}
 *     label="Export results"
 *   />
 *
 * Usage — async data fetch:
 *   <ExportMenu
 *     getData={async () => await fetchAllItems()}
 *     filename="items-full"
 *   />
 *
 * Usage — icon-only toolbar button:
 *   <ExportMenu data={rows} filename="tracks" iconOnly />
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Download, FileDown, FileSpreadsheet, Loader2, ChevronDown } from 'lucide-react';
import { exportToCsv } from '../../lib/reportExport';
import { exportToExcel } from '../../lib/excelService';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ExportFormat = 'csv' | 'excel' | 'json';

export interface ExportMenuProps {
  /** Static data array. Provide either this or getData. */
  data?: Record<string, unknown>[];
  /** Async function to fetch data at export time (for large / paginated sets). */
  getData?: () => Promise<Record<string, unknown>[]>;
  /** Base filename without extension */
  filename: string;
  /** Limit export to specific columns (in this order). Omit for all columns. */
  columns?: string[];
  /** Optional display name mapping: column key → header label */
  columnLabels?: Record<string, string>;
  /** Formats to show. Defaults to all three. */
  formats?: ExportFormat[];
  /** Show icon only (no label). Useful in tight toolbars. */
  iconOnly?: boolean;
  /** Button label. Defaults to "Export". */
  label?: string;
  /** Extra CSS classes on the trigger button */
  className?: string;
  /** Called after a successful export */
  onExportComplete?: (format: ExportFormat, rowCount: number) => void;
  /** Called on export error */
  onExportError?: (error: Error) => void;
  /** Disable all export actions */
  disabled?: boolean;
  /**
   * Extra entries under the file formats — a print view, charts as PNG.
   * Each is a plain action; the menu closes when one is chosen.
   */
  extraItems?: ExportExtraItem[];
}

export interface ExportExtraItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function projectRows(
  rows: Record<string, unknown>[],
  columns?: string[]
): Record<string, unknown>[] {
  if (!columns || columns.length === 0) return rows;
  return rows.map((r) => Object.fromEntries(columns.map((k) => [k, r[k]])));
}

function applyLabels(
  rows: Record<string, unknown>[],
  labels: Record<string, string> | undefined
): Record<string, unknown>[] {
  if (!labels || Object.keys(labels).length === 0) return rows;
  return rows.map((r) =>
    Object.fromEntries(
      Object.entries(r).map(([k, v]) => [labels[k] ?? k, v])
    )
  );
}

function downloadJson(rows: Record<string, unknown>[], filename: string): void {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

const FORMAT_META: Record<ExportFormat, { label: string; ext: string; icon: React.ReactNode }> = {
  csv: {
    label: 'CSV',
    ext: '.csv',
    icon: <FileDown className="h-4 w-4" />,
  },
  excel: {
    label: 'Excel',
    ext: '.xlsx',
    icon: <FileSpreadsheet className="h-4 w-4" />,
  },
  json: {
    label: 'JSON',
    ext: '.json',
    icon: <FileDown className="h-4 w-4" />,
  },
};

export function ExportMenu({
  data,
  getData,
  filename,
  columns,
  columnLabels,
  formats = ['csv', 'excel', 'json'],
  iconOnly = false,
  label = 'Export',
  className = '',
  onExportComplete,
  onExportError,
  disabled = false,
  extraItems = [],
}: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<ExportFormat | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const resolveData = useCallback(async (): Promise<Record<string, unknown>[]> => {
    const raw = getData ? await getData() : (data ?? []);
    const projected = projectRows(raw, columns);
    return applyLabels(projected, columnLabels);
  }, [data, getData, columns, columnLabels]);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (loading || disabled) return;
      setOpen(false);
      setLoading(format);

      try {
        const rows = await resolveData();

        if (rows.length === 0) {
          throw new Error('No data to export');
        }

        if (format === 'csv') {
          exportToCsv(
            `${filename}.csv`,
            rows as Record<string, string | number | boolean | null | undefined>[]
          );
        } else if (format === 'excel') {
          exportToExcel({
            filename: `${filename}.xlsx`,
            sheets: [{ name: 'Data', data: rows }],
            includeHeaders: true,
          });
        } else if (format === 'json') {
          downloadJson(rows, filename);
        }

        onExportComplete?.(format, rows.length);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Export failed');
        console.error('[ExportMenu] export failed:', error);
        onExportError?.(error);
      } finally {
        setLoading(null);
      }
    },
    [loading, disabled, resolveData, filename, onExportComplete, onExportError]
  );

  const isLoading = loading !== null;

  return (
    <div ref={menuRef} className="relative inline-block">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && !isLoading && setOpen((v) => !v)}
        disabled={disabled || isLoading}
        className={[
          'inline-flex items-center gap-1.5  border border-[var(--rule)] bg-[var(--bg-raised)]',
          'px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] shadow-sm',
          'hover:bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500',
          'disabled:opacity-50 disabled:pointer-events-none transition-colors',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        {!iconOnly && (
          <>
            <span>{isLoading ? 'Exporting…' : label}</span>
            {!isLoading && <ChevronDown className="h-3 w-3 opacity-60" />}
          </>
        )}
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          className={[
            'absolute z-50 mt-1 min-w-[140px]  border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg',
            'py-1 text-sm text-[var(--text-sec)]',
            // Align right if not icon-only to avoid overflow
            iconOnly ? 'right-0' : 'right-0',
          ].join(' ')}
          role="menu"
        >
          {formats.map((format) => {
            const meta = FORMAT_META[format];
            return (
              <button
                key={format}
                type="button"
                onClick={() => handleExport(format)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--surface)] transition-colors"
                role="menuitem"
              >
                <span className="text-[var(--text-ter)]">{meta.icon}</span>
                <span>{meta.label}</span>
                <span className="ml-auto text-xs text-[var(--text-ter)]">{meta.ext}</span>
              </button>
            );
          })}
          {extraItems.length > 0 && formats.length > 0 && <div className="my-1 border-t border-[var(--rule)]" />}
          {extraItems.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onSelect(); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--surface)] disabled:opacity-40 transition-colors"
              role="menuitem"
            >
              <span className="text-[var(--text-ter)]">{item.icon ?? <FileDown className="h-4 w-4" />}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Preset variants ────────────────────────────────────────────────────────────

/** Compact icon-only variant for use in table toolbars */
export function ExportIconButton(props: Omit<ExportMenuProps, 'iconOnly'>) {
  return <ExportMenu {...props} iconOnly />;
}

/** CSV-only shortcut — single button, no dropdown */
export function CsvExportButton({
  data,
  getData,
  filename,
  columns,
  label = 'CSV',
  className = '',
  disabled = false,
  onExportComplete,
  onExportError,
}: Pick<
  ExportMenuProps,
  'data' | 'getData' | 'filename' | 'columns' | 'label' | 'className' | 'disabled' | 'onExportComplete' | 'onExportError'
>) {
  return (
    <ExportMenu
      data={data}
      getData={getData}
      filename={filename}
      columns={columns}
      formats={['csv']}
      label={label}
      className={className}
      disabled={disabled}
      onExportComplete={onExportComplete}
      onExportError={onExportError}
    />
  );
}

export default ExportMenu;
